<template>
  <navbar-menu-tool
    v-model="isMenuOpen"
    :menu-data="{ name, position: 'left', allowOtherTop: true }"
    :button-bind="{ verboseName }"
    type="menu"
    has-close-header
    has-footer
  >
    <template #content>
      <v-row no-gutters>
        <v-col cols="12">
          <v-form v-model="isLayersItemsFormValid" ref="addedAreasForm">
            <v-row
              no-gutters
              v-for="(item, index) in layersItems"
              :key="index"
              class="align-center pt-3 px-2"
              style="gap: 0.2rem; flex-wrap: nowrap"
            >
              <v-col cols="auto" class="checkbox-wrapper">
                <v-checkbox v-model="item.checked" :disabled="isCalculationLoading" hide-details></v-checkbox>
              </v-col>
              <v-col cols="1" style="min-width: 100px; max-width:100%" class="flex-grow-1 flex-shrink-0">
                <data-input
                  v-model="item.layer_id"
                  :disabled="item.predefined"
                  @change="handleChangeLayer('addedAreasForm', item.buffer)"
                  v-bind="{
                    ...layerInputBind,
                    rules: [v => !!projectLayers.find(layer => layer.id === v)],
                  }"
                />
              </v-col>
              <v-col cols="1" style="min-width: 100px" class="flex-grow-0 flex-shrink-1">
                <data-input v-model="item.buffer" v-bind="getBufferProps(item.layer_id)" />
              </v-col>
              <v-col cols="auto" style="width: 24px">
                <v-icon v-if="!item.predefined" @click="deleteArea(index)" color="error">mdi-close</v-icon>
              </v-col>
            </v-row>
          </v-form>
          <v-form v-model="isNewLayerItemValid" ref="newAreaForm">
            <v-row no-gutters style="flex-wrap: nowrap; gap: 0.2rem" class="align-center pt-3 px-2">
              <v-col cols="auto" class="checkbox-wrapper">
                <v-checkbox v-model="newArea.checked" :disabled="true" hide-details></v-checkbox>
              </v-col>
              <v-col cols="1" style="min-width: 100px; max-width:100%" class="flex-grow-1 flex-shrink-0">
                <data-input
                  v-model="newArea.layer_id"
                  @change="handleChangeLayer('newAreaForm', newArea.buffer)"
                  v-bind="{ ...layerInputBind, rules: [v => !!v] }"
                />
              </v-col>
              <v-col cols="1" style="min-width: 100px" class="flex-grow-0 flex-shrink-1">
                <data-input v-model="newArea.buffer" v-bind="getBufferProps(newArea.layer_id)" />
              </v-col>
              <v-col cols="auto" style="width: 24px">
                <v-icon :color="isNewLayerItemValid ? 'success' : 'grey'" @click="pushArea">mdi-check</v-icon>
              </v-col>
            </v-row>
          </v-form>
          <v-form v-model="areAdditionalParamsValid">
            <v-row no-gutters style="flex-wrap: nowrap; gap: 0.2rem" class="align-center border-bottom py-4 mx-2">
              <v-col
                cols="1"
                style="min-width: 100px; max-width:100%"
                class="flex-grow-1 flex-shrink-0 text-left text-body-2 pl-1"
              >
                {{ $i18n.t('sidebar.usableArea.innerBuffer') }}:
              </v-col>
              <v-col cols="1" style="min-width: 100px" class="flex-grow-0 flex-shrink-1">
                <data-input v-model="innerBuffer" v-bind="bufferInputBind" :rules="[v => !!v || v == '0']" />
              </v-col>
              <v-col cols="auto" style="width: 24px" />
            </v-row>
          </v-form>
          <template v-if="isCalculationDone">
            <v-row no-gutters class="align-center py-3 px-2" style="gap: 0.5rem">
              <v-col cols="auto">
                <span class="font-weight-medium">{{ $i18n.t('sidebar.usableArea.results') }}</span>
              </v-col>
              <v-col cols="auto">
                <dots-menu
                  :items="[
                    { name: 'kml', customName: $i18n.t('sidebar.usableArea.saveAsKML') },
                    { name: 'datasource', customName: $i18n.t('sidebar.usableArea.saveToDatasource') },
                  ]"
                  @kml="saveToKML"
                  @datasource="() => (isDataSourceModalLoaded = isDataSourceModalVisible = true)"
                >
                  <template #activator="{ on }">
                    <v-icon v-on="on" color="primary" :disabled="!result.features?.length">mdi-content-save</v-icon>
                  </template>
                </dots-menu>
              </v-col>
            </v-row>
            <v-row no-gutters class="align-center px-2 pb-3" style="gap: 0.5rem">
              <table style="width: 100%">
                <tbody>
                  <tr v-for="[key, value] in Object.entries(result.values)" :key="key" class="text-left">
                    <td class="pr-2 text-body-2">{{ $i18n.t(`sidebar.usableArea.${key}`) }}:</td>
                    <td class="text-right">
                      <v-tooltip v-model="value.tooltip.m" bottom color="primary">
                        <template v-slot:activator="{ attrs }">
                          <v-chip @click="copyValue(key, 'm', value.value)" small v-bind="attrs">
                            {{ value.value }} m²
                          </v-chip>
                        </template>
                        <span>{{ $i18n.t('tooltips.valueCopied') }}</span>
                      </v-tooltip>
                    </td>
                    <td class="text-left">
                      <v-tooltip v-model="value.tooltip.h" bottom color="primary">
                        <template v-slot:activator="{ attrs }">
                          <v-chip @click="copyValue(key, 'h', (value.value / 10000).toFixed(2))" small v-bind="attrs">
                            {{ (value.value / 10000).toFixed(2) }} ha
                          </v-chip>
                        </template>
                        <span>{{ $i18n.t('tooltips.valueCopied') }}</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-row>
            <v-row no-gutters class="align-center px-2 py-2">
              <v-col cols="12">
                <v-data-table
                  :headers="resultHeaders"
                  height="auto"
                  class="usableAreaResultTable"
                  hide-default-footer
                  disable-pagination
                  fixed-header
                  :items="result.features"
                  :item-class="getTableRowClass"
                  dense
                >
                  <template #item.visible="{ item: feature }">
                    <v-checkbox v-model="feature.visible" @change="toggleFeature" hide-details class="ma-0" />
                  </template>
                  <template #item.aream="{ item: feature }">
                    <v-tooltip v-model="feature.tooltip.m" bottom color="primary">
                      <template v-slot:activator="{ attrs }">
                        <v-chip
                          @click="copyValue(feature.id, 'm', feature.properties.usable_area_m)"
                          small
                          v-bind="attrs"
                        >
                          {{ feature.properties.usable_area_m }}
                        </v-chip>
                      </template>
                      <span>{{ $i18n.t('tooltips.valueCopied') }}</span>
                    </v-tooltip>
                  </template>
                  <template #item.areah="{ item: feature }">
                    <v-tooltip v-model="feature.tooltip.h" bottom color="primary">
                      <template v-slot:activator="{ attrs }">
                        <v-chip
                          @click="copyValue(feature.id, 'h', (feature.properties.usable_area_m / 10000).toFixed(2))"
                          small
                          v-bind="attrs"
                        >
                          {{ (feature.properties.usable_area_m / 10000).toFixed(2) }}
                        </v-chip>
                      </template>
                      <span>{{ $i18n.t('tooltips.valueCopied') }}</span>
                    </v-tooltip>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </template>
        </v-col>
        <main-input-dialog
          v-if="isDataSourceModalLoaded"
          :is-visible.sync="isDataSourceModalVisible"
          :loading="saveResultLoading"
          :title-translation="$i18n.t('dialog.title.saveResultToDataSource')"
          dialog-width="30%"
          @agree="saveToDatasource"
          :inputs="dataSourceModalInputs"
        >
          <template #[`dataSourceName.input`]="data">
            <v-col cols="6">
              <data-input v-model="resultDataSourceName" v-bind="data" />
            </v-col>
          </template>
        </main-input-dialog>
      </v-row>
    </template>
    <template #content.header.append>
      <v-tooltip bottom max-width="500" color="font">
        <template #activator="{ on }">
          <v-icon v-on="on" small color="primary">
            mdi-information
          </v-icon>
        </template>
        {{ $i18n.t('sidebar.usableArea.subtitle') }}
      </v-tooltip>
    </template>
    <template #content.footer>
      <base-button
        :translation-path="isCalculationDone ? 'dialog.agreeRunAgain' : 'dialog.agreeRun'"
        class="ml-3"
        @click="handleCalculate"
        :disabled="!isCalculatingValid"
        :loading="isCalculationLoading"
      />
      <base-button
        v-if="isCalculationDone"
        @click="reset"
        :disabled="isCalculationLoading"
        translation-path="dialog.reset"
        cancel
      />
    </template>
  </navbar-menu-tool>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { isNumber } from 'lodash';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import NavbarMenuTool from '@/components/NavbarMenuTool';

import { KML, GeoJSON } from 'ol/format';
import { Style, Fill, Stroke } from 'ol/style';

export default {
  name: 'TheNavbarToolsPanelUsableArea',
  components: {
    DataInput,
    DotsMenu,
    NavbarMenuTool,
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    verboseName: {
      type: String,
    },
    name: {
      type: String,
    },
  },
  computed: {
    currentLayer: get('layers/currentLayer'),
    currentLayerId() {
      return this.currentLayer?.id;
    },
    isCurrentLayerPolygon() {
      return ['polygon', 'multipolygon'].includes(this.currentLayer?.geometry_type);
    },
    selectedFeatures: get('map/selectedFeatures@:currentLayerId'),

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers.filter(layer => !!layer.geometry_type);
    },

    bufferInputBind() {
      return {
        name: 'buffer',
        dataType: { name: 'float' },
        hideDetails: true,
        label: this.$i18n.t('sidebar.usableArea.buffer'),
        readonly: this.isCalculationLoading,
      };
    },
    layerInputBind() {
      return {
        name: 'layer_id',
        dataType: { name: 'select' },
        items: this.projectLayers,
        itemText: 'name',
        itemValue: 'id',
        hideDetails: true,
        label: this.$i18n.t('sidebar.usableArea.layerToCalculate'),
        readonly: this.isCalculationLoading,
      };
    },

    isCalculatingValid() {
      return (
        this.areAdditionalParamsValid &&
        this.isCurrentLayerPolygon &&
        this.selectedFeatures?.length > 0 &&
        this.selectedFeatures?.length <= 1000
      );
    },

    dataSources: get('layers/metadata'),
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources).filter(
        dataSource => ['multipolygon'].includes(dataSource.geometry_type) && dataSource.scope === 'core'
      );
    },
    dataSourceModalInputs() {
      return [
        {
          name: 'dataSourceName',
          translationPath: 'dialog.dataSource',
          subtitle: 'sidebar.buffer.dataSourceSubtitle',
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesArray,
          itemValue: 'name',
          itemText: 'verbose_name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },

    resultHeaders() {
      const defaultProps = {
        sortable: false,
        align: 'center',
      };
      return [
        {
          text: '',
          value: 'visible',
          ...defaultProps,
        },
        {
          text: this.$i18n.t('sidebar.usableArea.index'),
          value: 'id',
          ...defaultProps,
        },
        {
          text: this.$i18n.t('sidebar.usableArea.aream'),
          value: 'aream',
          ...defaultProps,
        },
        {
          text: this.$i18n.t('sidebar.usableArea.areah'),
          value: 'areah',
          ...defaultProps,
        },
      ];
    },
  },
  data: () => ({
    isMenuOpen: false,

    areAdditionalParamsValid: false,
    isNewLayerItemValid: false,
    isLayersItemsFormValid: false,

    newArea: {
      checked: true,
      layer_id: null,
      buffer: null,
    },
    layersItems: [],
    innerBuffer: 0,

    currentTaskId: null,
    isCalculationDone: false,
    isCalculationLoading: false,
    result: {
      features: [],
      values: {
        areaSum: {
          tooltip: {
            h: false,
            m: false,
          },
          value: null,
        },
        usableArea: {
          tooltip: {
            h: false,
            m: false,
          },
          value: null,
        },
      },
    },
    isDataSourceModalLoaded: false,
    isDataSourceModalVisible: false,
    saveResultLoading: false,
    resultDataSourceName: null,
  }),
  methods: {
    addFeatureAction: call('layers/addDatasourceFeature'),
    getUsableArea: call('externalSystems/getUsableArea'),
    stopCeleryTask: call('celeryTasks/stopCeleryTask'),
    deleteArea(index) {
      this.layersItems.splice(index, 1);
    },
    pushArea() {
      if (!this.isNewLayerItemValid) return;
      this.layersItems.push({
        checked: true,
        // TODO 'predefined' (placeholder name) is future attribute for part 2
        predefined: false,
        layer_id: this.newArea.layer_id,
        buffer: this.newArea.buffer,
      });
      this.newArea.layer_id = null;
      this.$refs['newAreaForm']?.resetValidation();
    },

    handleChangeLayer(formId, bufferValue) {
      if (!(bufferValue || bufferValue === 0)) return;
      this.$nextTick(() => {
        this.$refs[formId]?.validate();
      });
    },
    getBufferProps(layerId) {
      const layerGeometry = this.projectLayers.find(layer => layer.id === layerId)?.geometry_type;
      const canGetZeroBuffer = layerGeometry ? ['multipolygon', 'polygon'].includes(layerGeometry) : true;
      return {
        ...this.bufferInputBind,
        rules: [v => (canGetZeroBuffer ? parseFloat(v) >= 0 : !!v)],
      };
    },

    async handleCalculate() {
      this.isCalculationDone = false;
      this.isCalculationLoading = true;
      const payload = {
        internal_data: this.layersItems
          .filter(item => item.checked)
          .map(item => {
            return {
              layer_id: item.layer_id,
              buffer: item.buffer,
            };
          }),
        buffer: this.innerBuffer,
        layer: {
          ids: this.selectedFeatures,
          layer_id: this.currentLayerId,
        },
      };
      try {
        this.currentTaskId = await this.getUsableArea(payload);
        this.$globalHub.$emit('handleCeleryTask', {
          currentTaskId: this.currentTaskId,
          taskSuccessResultParams: {
            type: 'data',
          },
          taskFailureSnackbar: {
            message: this.$i18n.t('snackbar.usableAreaError'),
          },
          taskFailureCallback: this.endCalculationsCallback,
          taskSuccessCallback: r => this.handleUsableAreaTaskResult(r),
        });
      } catch {
        this.endCalculationsCallback();
      }
    },
    endCalculationsCallback() {
      this.currentTaskId = null;
      this.isCalculationLoading = false;
    },
    handleUsableAreaTaskResult(r) {
      let usableArea = 0;
      const resultFeatures = r.features.map((feature, idx) => {
        usableArea += feature.usable_area;
        return {
          type: 'Feature',
          properties: { usable_area_m: feature.usable_area.toFixed(2) },
          id: idx + 1,
          geometry: feature.geometry,
          tooltip: {
            h: false,
            m: false,
          },
          visible: true,
        };
      });
      this.handleGeometryInstance(resultFeatures);
      this.result = {
        features: resultFeatures,
        values: {
          areaSum: {
            tooltip: {
              h: false,
              m: false,
            },
            value: r.total_area.toFixed(2),
          },
          usableArea: {
            tooltip: {
              h: false,
              m: false,
            },
            value: usableArea.toFixed(2),
          },
        },
      };
      localStorage.setItem(
        'usable-area-config',
        JSON.stringify({
          items: this.layersItems,
          innerBuffer: this.innerBuffer,
        })
      );
      this.isCalculationDone = true;
      this.endCalculationsCallback();
    },
    handleGeometryInstance(features) {
      if (!features?.length) {
        this.$root.$emit('clearVisibleGeomLayerAndFitView', 'usableAreaLayer');
        return;
      }
      this.$root.$emit(
        'createFeaturesLayer',
        { type: 'FeatureCollection', features },
        'usableAreaLayer',
        {},
        {
          style: new Style({
            fill: new Fill({
              color: 'rgba(200, 0, 0, 0.6)',
            }),
            stroke: new Stroke({
              color: 'rgba(255, 0, 0, 1)',
              width: 2,
            }),
          }),
        },
        true
      );
    },
    reset() {
      this.handleGeometryInstance(null);
      this.isCalculationDone = false;
    },

    getMultiPolygonGeometry() {
      return {
        coordinates: this.result.features
          .filter(feature => feature.visible)
          .map(feature => feature.geometry.coordinates),
        crs: {
          type: 'name',
          properties: {
            name: this.$_config.defaultEpsg,
          },
        },
        type: 'MultiPolygon',
      };
    },
    saveToKML() {
      let kmlString = `<?xml version="1.0" encoding="utf-8" ?>${new KML().writeFeatures(
        new GeoJSON().readFeatures(this.getMultiPolygonGeometry()),
        {
          featureProjection: this.$_config.defaultEpsg,
          dataProjection: 'EPSG:4326',
        }
      )}`;
      const file = new Blob([kmlString], {
        type: 'text/xml;charset=utf-8;',
      });
      this.$_saveFile(URL.createObjectURL(file), `usableAreaCalc.kml`);
    },
    async saveToDatasource() {
      this.saveResultLoading = true;
      const dataSource = this.dataSources[this.resultDataSourceName];
      const payload = {
        datasource: this.resultDataSourceName,
        body: {
          [dataSource.attributes_schema.geometry_name]: this.getMultiPolygonGeometry(),
        },
      };
      try {
        await this.addFeatureAction(payload);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('sidebar.measurementsModule.successSaveFeatures'),
        });
        this.projectLayers.forEach(layer => {
          if (layer.data_source_name === this.resultDataSourceName) {
            this.$root.$emit('refreshMvtSource', layer.id);
          }
        });
        this.isDataSourceModalVisible = false;
      } finally {
        this.saveResultLoading = false;
      }
    },
    copyValue(key, type, value) {
      navigator.clipboard.writeText(value);
      if (isNumber(key)) this.result.features.find(feature => feature.id === key).tooltip[type] = true;
      else this.result.values[key].tooltip[type] = true;
      setTimeout(() => {
        if (isNumber(key)) this.result.features.find(feature => feature.id === key).tooltip[type] = false;
        else this.result.values[key].tooltip[type] = false;
      }, 1500);
    },
    toggleFeature() {
      const filteredFeatures = this.result.features.filter(feature => feature.visible);
      this.handleGeometryInstance(filteredFeatures);
      this.result.values.usableArea.value = filteredFeatures
        .reduce((total, current) => {
          total += parseFloat(current.properties.usable_area_m);
          return total;
        }, 0)
        .toFixed(2);
    },
    getTableRowClass(e) {
      if (!e.visible) return 'table-row-disabled';
      return '';
    },
  },
  watch: {
    projectLayers: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.$refs['addedAreasForm']?.validate();
        });
      },
    },
  },
  mounted() {
    const config = JSON.parse(localStorage.getItem('usable-area-config') || '{}');
    this.innerBuffer = config.innerBuffer || 0;
    this.layersItems = config.items || [];
  },
  beforeDestroy() {
    if (this.currentTaskId) this.stopCeleryTask(this.currentTaskId);
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .checkbox-wrapper .v-input--selection-controls {
    margin: 0 !important;
    padding: 0 !important;
  }
  .checkbox-wrapper .v-input--selection-controls__input {
    margin: 0 !important;
  }
  .table-row-disabled {
    color: #8a8a8a;
    .v-chip {
      color: #8a8a8a;
      background: #ebebeb;
    }
  }
}
.usableAreaResultTable {
  @include border();
  ::v-deep {
    tr {
      height: 36px;
    }
  }
  div {
    overflow-y: hidden;
  }
}
.border-bottom {
  @include border(bottom);
}
</style>
